<template>
  <layout-navigation>
    <section
      :class="[
        $style.page,
        {
          [$style.fit]: isParallel,
        },
      ]"
    >
      <header
        v-if="!isMapData"
        :class="$style.header"
      >
        <div :class="$style.control">
          <store-based-date-time-picker :clearable="false" />

          <div
            v-if="isParallel"
            :class="$style.control"
          >
            <div
              :class="$style.box"
              @click="changeMode(false)"
            >
              <img
                :class="$style.arrow"
                src="@/assets/images/icons/navigation/menu.svg"
                :title="$t('computers.sequentialMode')"
              >
            </div>
            <div :class="[$style.box, $style.activeBox]">
              <img
                :class="$style.arrow"
                src="@/assets/images/icons/navigation/menu-rotated-selected.svg"
                :title="$t('computers.parallelMode')"
              >
            </div>
          </div>
          <div
            v-else
            :class="[$style.control, { [$style.hide]: !hide }]"
          >
            <div :class="[$style.box, $style.activeBox]">
              <img
                :class="$style.arrow"
                src="@/assets/images/icons/navigation/menu-selected.svg"
                :title="$t('computers.sequentialMode')"
              >
            </div>
            <div
              :class="$style.box"
              @click="changeMode(true)"
            >
              <img
                :class="$style.arrow"
                src="@/assets/images/icons/navigation/menu-rotated.svg"
                :title="$t('computers.parallelMode')"
              >
            </div>
          </div>

          <div :class="$style.control">
            <refresh
              :class="$style.mr"
              @click="refresh"
            />
          </div>
        </div>
        <div :class="$style.container">
          <div :class="$style.modeName">
            {{ currentMode }}
            <span
              v-if="countOfComputers"
              :class="$style.countComp"
            >
              {{ $t('computers.selectOneUser') }}
            </span>
          </div>
          <div :class="$style.container">
            <div class="searchLogs">
              <treeselect
                v-model="selectedComputers"
                :multiple="true"
                :options="computers"
                :clearable="true"
                :open-on-click="true"
                :placeholder="$t('computers.userSelection')"
                value-consists-of="LEAF_PRIORITY"
                :default-expand-level="1"
                :normalizer="normalizer"
                :disable-branch-nodes="false"
              />
            </div>
            <div class="searchLogs">
              <treeselect
                v-model="selectedLogs"
                :multiple="true"
                :options="logs"
                :clearable="true"
                :open-on-click="true"
                :placeholder="$t('computers.categorySelection')"
                value-consists-of="LEAF_PRIORITY"
                :default-expand-level="1"
                :normalizer="normalizer"
              />
            </div>

            <button
              :class="[$style.button, { [$style.disabledButton]: disabledButton }]"
              :disabled="disabledButton"
              @click="getFeed()"
            >
              {{ $t('global.show') }}
            </button>
          </div>
        </div>
      </header>
      <div :class="$style.wrapper">
        <search
          v-show="!isTableLoading"
          :class="{ [$style.hide]: !tableData.length }"
        />
        <div
          v-show="isTableLoading || isLoaderNeeded"
          :class="$style.loader"
        >
          <loader />
        </div>
        <div
          v-show="isTableReady"
          :class="$style.ready"
        >
          {{ $t('computers.allEventsLoaded') }}
          <img
            src="@/assets/images/icons/navigation/confirm.svg"
            :class="$style.saveIcon"
          >
        </div>
        <export
          v-show="!isTableLoading"
          :selected-computers="selectedComputers"
          :selected-logs="selectedLogs"
          :class="{ [$style.hide]: isParallel || !filtredData.length }"
        />
      </div>
      <group-table
        v-if="!isParallel && filtredData.length"
        id="printable"
        ref="advancedTable"
        :table-data="paginationData"
        :empty-result-string="$t('computers.noDataForSelectedPeriod')"
        :columns="columns"
        :is-feed="true"
        :table-name="'advanced'"
        @changeSort="changeSort"
        @filterClick="filterClick"
      />
      <paginate
        v-if="!isPdfPage && !isParallel && filtredData.length > perData"
        v-model="dataPage"
        :page-count="countData"
        :click-handler="(page) => setDataPage(page)"
        :prev-text="'‹'"
        :next-text="'›'"
        :container-class="$style.pagination"
        :page-class="$style.page2"
        :prev-class="$style.page2"
        :next-class="$style.page2"
        :active-class="$style.pageActive2"
      />

      <timeline
        v-if="isParallel"
        :data="filtredData"
        :selected-logs="selectedLogs"
        :is-view-timline="true"
        @openFullScreen="openModal()"
      />
      <modal
        v-if="isShowModal"
        :title="$t('computers.allData')"
        :scroll-lock="true"
        :fullscreen="true"
        @close="close"
      >
        <timeline
          v-if="isParallel"
          :data="filtredData"
          :selected-logs="selectedLogs"
          :is-show-modal="isShowModal"
          :is-view-timline="true"
        />
      </modal>
    </section>
  </layout-navigation>
</template>

<script>
  import Search from '@/components/pages/computers/log/screenshots/components-header/search.vue';
  import Export from '@/components/pages/computers/log/screenshots/components-header/export.vue';
  import Timeline from '@/views/computers/advanced/timeline.vue';
  import LayoutNavigation from '@/layouts/navigation.vue';
  import Refresh from '@/components/common/filter/refresh.vue';
  import StoreBasedDateTimePicker from '@/components/common/filter/store-based-date-time-picker.vue';
  import Treeselect from '@riophae/vue-treeselect';
  import { getComputers, getScreenshotLink, getClipboardFileLink, getRelationsMapLogs } from '@/api/methods/computers';
  // eslint-disable-next-line
  import Worker from 'worker-loader!@/components/common/logs/advanced-feed-worker';
  import { logTypes } from '@/assets/scripts/dictionaries';
  import GroupTable from '@/components/common/table/tableWithGrouping.vue';
  import Loader from '@/components/common/blocks/loader.vue';
  import { convertSecondsToDays, getDateUTCFromBuildReport, getDateUTC } from '@/helpers/format-date';
  import { messengerName, directionName } from '@/components/pages/computers/log/messengers/filters';
  import { status } from '@/components/pages/computers/log/programs/utils';
  import { action as instProgAction } from '@/components/pages/computers/log/installed-programs/utils';
  import { action as filesAction } from '@/components/pages/computers/log/files/utils';
  import { type } from '@/components/pages/computers/log/web-mail/utils';
  import { type as clipboardType } from '@/components/pages/computers/log/clipboard/utils';
  import { source, action, available } from '@/components/pages/computers/log/shadow-copyings/utils';
  import { driveType, action as drivesAction } from '@/components/pages/computers/log/external-drives/utils';
  import { action as netFilesAction } from '@/components/pages/computers/log/network-files/utils';
  import { action as hardwareAction } from '@/components/pages/computers/log/hardware-changes/utils';
  import { action as computerAction } from '@/components/pages/computers/log/computer/utils';
  import { action as networkAction } from '@/components/pages/computers/log/network-accesses/utils';
  import { dateTime } from '@/helpers/filters';
  import { LogData } from '@/domains/log/data';
  // import workerBasedRequestMixin from '@/components/common/logs/mixins/workerBasedRequestMixin';
  import Modal from '@/components/common/modal/index.vue';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import Paginate from 'vuejs-paginate';

  import { v4 as uuidv4 } from 'uuid';
  import dayjs from 'dayjs';

  const filters = [(item) => dateTime(item.date), 'username', 'logName', 'details'];

  export default {
    components: {
      Search,
      Export,
      LayoutNavigation,
      Refresh,
      StoreBasedDateTimePicker,
      Treeselect,
      GroupTable,
      Loader,
      Timeline,
      Modal,
      Paginate,
    },
    // mixins: [workerBasedRequestMixin],

    data() {
      return {
        perData: 10,
        dataPage: 1,
        isMapData: false,
        hide: true,
        isParallel: false,
        isShowModal: false,
        parallelData: [],
        selectedLogs: [],
        logs: [],
        computers: [],
        selectedComputers: [],
        selectedComputer: null,
        pages: [],
        tableData: [],
        filtredData: [],
        workers: [],
        workersNumber: 10,
        columns: [
          {
            name: this.$t('computers.columns.eventTime'),
            width: '10%',
            widthNum: 10,
            field: 'date',
            sort: 1,
            index: 0,
            filter: [],
            selected: [],
            showFilter: false,
          },
          {
            name: this.$t('computers.columns.username'),
            width: '10%',
            widthNum: 10,
            field: 'username',
            sort: 0,
            index: 1,
            filter: [],
            selected: [],
            showFilter: false,
          },
          {
            name: this.$t('computers.columns.category'),
            width: '4%',
            widthNum: 10,
            field: 'logName',
            sort: 0,
            index: 2,
            filter: [],
            selected: [],
            showFilter: false,
          },

          {
            name: this.$t('computers.columns.details'),
            width: '70%',
            widthNum: 70,
            field: 'details',
            sort: 0,
            index: 3,
            filter: [],
            selected: [],
            showFilter: false,
          },
        ],
        logTypes: {
          1: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.keys'), field: 'keys' },
            { text: this.$t('logTypes.path'), field: 'path' },
          ],
          2: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.path'), field: 'path' },
            { text: this.$t('logTypes.screenshot'), field: 'ssh' },
          ],
          3: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.path'), field: 'path' },
            { text: this.$t('logTypes.category'), field: 'category' },
            { text: this.$t('logTypes.program'), field: 'name' },
            { text: this.$t('logTypes.action'), field: 'status' },
          ],
          4: [
            { text: this.$t('logTypes.path'), field: 'path' },
            { text: this.$t('logTypes.action'), field: 'action' },
            { text: this.$t('logTypes.size'), field: 'size' },
            { text: this.$t('logTypes.md5'), field: 'crc' },
          ],
          5: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.dataType'), field: 'type' },
            { text: this.$t('logTypes.clipboardContent'), field: 'text' },
            { text: this.$t('logTypes.screenshot'), field: 'ssh' },
            { text: this.$t('logTypes.file'), field: 'file' },
          ],
          6: [
            { text: this.$t('logTypes.printer'), field: 'printer' },
            { text: this.$t('logTypes.document'), field: 'document' },
            { text: this.$t('logTypes.pagesPrinted'), field: 'pagesPrinted' },
            { text: this.$t('logTypes.copies'), field: 'copies' },
            { text: this.$t('logTypes.computer'), field: 'computer' },
            { text: this.$t('logTypes.cost'), field: 'cost' },
            { text: this.$t('logTypes.file'), field: 'file' },
          ],
          7: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.action'), field: 'action' },
            { text: this.$t('logTypes.path'), field: 'path' },
          ],
          8: [
            { text: this.$t('logTypes.action'), field: 'action' },
            { text: this.$t('logTypes.driveLetter'), field: 'driveChar' },
            { text: this.$t('logTypes.driveLabel'), field: 'driveLabel' },
            { text: this.$t('logTypes.driveType'), field: 'driveType' },
            { text: this.$t('logTypes.filesystem'), field: 'filesystem' },
            { text: this.$t('logTypes.serialNumber'), field: 'serial' },
          ],
          10: [
            { text: this.$t('logTypes.caption'), field: 'caption' },
            { text: this.$t('logTypes.category'), field: 'category' },
            { text: this.$t('logTypes.link'), field: 'link' },
            { text: this.$t('logTypes.durationOnSite'), field: 'duration' },
          ],
          11: [{ text: this.$t('logTypes.action'), field: 'action' }],
          14: [
            { text: this.$t('logTypes.filename'), field: 'filename' },
            { text: this.$t('logTypes.filesize'), field: 'filesize' },
            { text: this.$t('logTypes.fileLocation'), field: 'available' },
            { text: this.$t('logTypes.sourceType'), field: 'source' },
            { text: this.$t('logTypes.md5'), field: 'crc' },
            { text: this.$t('logTypes.file'), field: 'file' },
          ],
          15: [
            { text: this.$t('logTypes.dateSent'), field: 'dateSend' },
            { text: this.$t('logTypes.subject'), field: 'subject' },
            { text: this.$t('logTypes.from'), field: 'from' },
            { text: this.$t('logTypes.to'), field: 'to' },
            { text: this.$t('logTypes.text'), field: 'text' },
            { text: this.$t('pages.attachedFiles'), field: 'attachments' },
          ],
          18: [
            { text: this.$t('logTypes.computer'), field: 'computerName' },
            { text: this.$t('logTypes.action'), field: 'action' },
          ],
          19: [
            { text: this.$t('logTypes.filepath'), field: 'filepath' },
            { text: this.$t('logTypes.action'), field: 'action' },
            { text: this.$t('logTypes.fileAccess'), field: 'fileaccess' },
          ],
          20: [
            { text: this.$t('logTypes.dateSent'), field: 'dateSend' },
            { text: this.$t('logTypes.subject'), field: 'subject' },
            { text: this.$t('logTypes.from'), field: 'from' },
            { text: this.$t('logTypes.to'), field: 'to' },
            { text: this.$t('logTypes.text'), field: 'text' },
            { text: this.$t('logTypes.messageType'), field: 'type' },
            { text: this.$t('logTypes.mailServer'), field: 'server' },
          ],
          21: [
            { text: this.$t('logTypes.url'), field: 'url' },
            { text: this.$t('logTypes.host'), field: 'host' },
          ],
          22: [
            { text: this.$t('logTypes.buddy'), field: 'buddy' },
            { text: this.$t('logTypes.socialNetwork'), field: 'socialDomain' },
            { text: this.$t('logTypes.text'), field: 'text' },
          ],
          23: [
            { text: this.$t('logTypes.messenger'), field: 'messenger' },
            { text: this.$t('logTypes.interlocutor'), field: 'interlocutor' },
            { text: this.$t('logTypes.messageType'), field: 'direction' },
            { text: this.$t('logTypes.text'), field: 'text' },
            { text: this.$t('logTypes.usernameInMessenger'), field: 'usernameMessenger' },
          ],
          25: [
            { text: this.$t('logTypes.action'), field: 'action' },
            { text: this.$t('logTypes.hardwareType'), field: 'hardwareType' },
            { text: this.$t('logTypes.description'), field: 'description' },
          ],
          26: [
            { text: this.$t('logTypes.searchEngine'), field: 'engine' },
            { text: this.$t('logTypes.queryText'), field: 'query' },
          ],
        },

        lastIds: {},
        pagesDone: 0,
        pagesNumber: undefined,
      };
    },
    computed: {
      isLoaderNeeded() {
        return this.$store.getters['loader/isLoaderNeeded'];
      },
      isPdfPage() {
        return this.$route.query.pdf === 'true';
      },
      countData() {
        return Math.ceil(this.filtredData.length / this.perData);
      },
      paginationData() {
        if (this.isPdfPage) {
          return this.filtredData;
        }
        const start = (this.dataPage - 1) * this.perData;
        const end = start + this.perData;
        return this.filtredData.slice(start, end);
      },
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      currentMode() {
        return this.isParallel ? this.$t('computers.parallel') : this.$t('computers.sequential');
      },
      countOfComputers() {
        return this.isParallel && this.selectedComputers.length > 1;
      },
      disabledButton() {
        return this.selectedComputers.length === 0 || this.countOfComputers;
      },
    },
    watch: {
      '$route.query': {
        handler(query, oldQuery) {
          if (query?.filter !== oldQuery?.filter) {
            this.getTableData();
            this.dataPage = 1;
          }
        },
      },
      isTableReady(newVal) {
        const { record, filter } = this.$route.query;
        if (newVal && record) {
          this.scrollToEvent(record);
        }
        if (newVal && filter) {
          this.$nextTick(() => {
            this.getTableData();
          });
        }
      },
      tableData() {
        this.filtredData = this.tableData;
      },
      filtredData() {
        if (this.isTableLoading) return;
        this.$store.commit('logs/printData/setData', this.filtredData);
      },
      workers: {
        handler(newWorkers) {
          const worker = newWorkers.find((one) => one.isBusy === false);

          if (worker) {
            const index = this.pages.findIndex((i) => i.done === false);
            if (index > -1) {
              worker.isBusy = true;
              this.pages[index].done = true;
              const { dateFrom, dateTo, log } = this.pages[index];
              const workerData = {
                index,
                computerIds: this.selectedComputers,
                logTypeSlug: log,
                dateFrom,
                dateTo,
              };
              worker.worker.postMessage(JSON.stringify(workerData));
            }
          }
        },
        deep: true,
      },
    },
    async created() {
      this.whenUpdated();

      const { isMapData, logIds, computer, logId } = this.$route.query;
      const advancedLogs = this.$store.getters['user/allowedTypes'].slice(0, -1);

      if (isMapData) {
        await this.initMapData(isMapData, logIds);
      } else {
        await this.initLogs(advancedLogs);

        if (!this.isPdfPage) {
          await this.initComputers();
        }
      }

      if (this.isPdfPage) {
        await this.initPdfPage();
      }

      if (computer && logId) {
        this.selectRouteData(computer, logId, advancedLogs);
        this.getFeed();
      }
    },

    mounted() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      this.$store.commit('logs/printData/setColumns', this.columns);
    },
    methods: {
      async initMapData(isMapData, logIds) {
        this.isMapData = isMapData;

        const logData = await getRelationsMapLogs(JSON.parse(logIds));

        logData.forEach((item) => {
          this.onDataRecieved({ items: [item] });
        });
      },

      async initLogs(advancedLogs) {
        this.logs = [
          {
            id: 0,
            name: this.$t('global.selectAll'),
            children: advancedLogs,
          },
        ];
      },
      async initComputers() {
        const result = await getComputers();

        if (result?.tree) {
          result.tree.forEach((item) => this.changeId(item));
        }

        this.computers = [
          {
            id: 0,
            name: this.$t('global.selectAll'),
            children: result?.tree || [],
          },
        ];
      },
      async initPdfPage() {
        this.selectedComputers = this.getStorageSelection('selectedComputers');
        this.selectedLogs = this.getStorageSelection('selectedLogs');

        await this.getFeed();

        await new Promise((resolve) => {
          setTimeout(resolve, 2000);
        });

        // eslint-disable-next-line no-underscore-dangle
        window.__PDF_READY__ = true;
      },
      getStorageSelection(key) {
        const value = JSON.parse(localStorage.getItem(key));
        return value?.length ? value : [];
      },

      selectRouteData(computer, logId, advancedLogs) {
        this.selectedComputers.push(+computer);

        if (logId === 'allLogs') {
          this.selectedLogs = advancedLogs.map(({ id }) => id);
          return;
        }

        this.selectedLogs.push(+logId);
      },
      async scrollToEvent(eventId) {
        let currentIdx = -1;
        let currentItem = null;

        this.filtredData.some((item, index) => {
          const id = item?.idRecord ?? item?.id;

          if (+id === +eventId) {
            currentIdx = index;
            currentItem = item;
            return true;
          }

          return false;
        });

        if (currentIdx === -1) return;

        const currentPage = Math.floor(currentIdx / this.perData) + 1;
        if (currentPage !== this.dataPage) this.setDataPage(currentPage);

        const { advancedTable } = this.$refs;
        if (advancedTable && typeof advancedTable.scrollToRow === 'function') {
          advancedTable.scrollToRow(currentItem);
        }
      },
      setDataPage(page) {
        this.dataPage = page;
      },
      changeMode(value) {
        this.isParallel = value;
        if (this.countOfComputers) {
          this.tableData = [];
        }
      },
      openModal() {
        this.isShowModal = true;
        this.$router.replace({
          query: {
            ...this.$route.query,
            modal: null,
          },
        });
      },
      close() {
        this.isShowModal = false;
        const query = { ...this.$route.query };
        delete query.modal;

        this.$router.replace({
          query,
        });
      },
      getTableData() {
        this.filtredData = this.tableData;

        let items = [...this.filtredData];
        items = items.filter((element) => element !== undefined);

        if (items.length) {
          this.filtredData = LogData.filterTable(items, filters, this.$route.query.filter);
        }
      },
      changeId(item) {
        if (item.type === 'folder') item.id = `folder${item.id}`;
        if (item.children) item.children.forEach((child) => this.changeId(child));
      },
      filterClick(column) {
        const value = column.showFilter;

        column.showFilter = !value;

        if (column.showFilter) {
          window.addEventListener('click', (e) => {
            if (
              !document.getElementById(`columnFilter${column.field}`).contains(e.target) &&
              !document.getElementById(`filterIcon${column.field}`).contains(e.target)
            ) {
              column.showFilter = false;
              window.removeEventListener('click', e);
            }
          });
        }
      },
      changeSort(column) {
        const { sort } = column;
        this.columns.forEach((col) => {
          col.sort = 0;
        });

        if (sort === 1) {
          column.sort = 2;
        } else column.sort = 1;
      },
      async getFeed() {
        if (this.selectedLogs.length || this.countOfComputers) {
          const initDate = getDateUTCFromBuildReport(this.$route.query.dateFrom, this.$route.query.dateTo);
          this.dateFrom = initDate.dateFrom;
          this.dateTo = initDate.dateTo;

          if (this.selectedComputers.length) {
            this.pagesDone = 0;
            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);

            for (let i = 0; i < this.selectedLogs.length; i++) {
              const { slug } = logTypes.find((log) => log.id === this.selectedLogs[i]);
              const res = {};
              for (let j = 0; j < this.selectedComputers.length; j++) {
                let lastId = 0;
                if (this.selectedLogs[i] !== 23) {
                  lastId = this.$store.getters['historyLogs/getLastId'](slug, this.selectedComputers[j]);
                } else {
                  const lastText = this.$store.getters['historyLogs/getLastId'](
                    'messengerstext',
                    this.selectedComputers[j],
                  );
                  const lastFile = this.$store.getters['historyLogs/getLastId'](
                    'messengersfile',
                    this.selectedComputers[j],
                  );

                  lastId = lastText > lastFile ? lastText : lastFile;
                }
                res[this.selectedComputers[j]] = lastId;
              }
              this.lastIds[this.selectedLogs[i]] = res;
            }

            const days = this.dateTo.diff(this.dateFrom, 'day');
            this.pagesNumber = (days + 1) * this.selectedLogs.length;

            this.pages = [];
            this.tableData = [];

            let dateFromPayload = this.dateFrom;
            let dateToPayload = this.dateTo;
            for (let j = 0; j < days + 1; j++) {
              const currentDay = this.dateTo.subtract(j, 'day');

              if (days > 0) {
                const currentDate = getDateUTC(currentDay, currentDay);
                if (j === 0) {
                  dateFromPayload = currentDate.dateFrom;
                  dateToPayload = this.dateTo;
                } else if (j === days) {
                  dateFromPayload = this.dateFrom;
                  dateToPayload = currentDate.dateTo;
                } else {
                  dateFromPayload = currentDate.dateFrom;
                  dateToPayload = currentDate.dateTo;
                }
              }

              for (let i = 0; i < this.selectedLogs.length; i++) {
                const { slug } = logTypes.find((log) => log.id === this.selectedLogs[i]);

                const payload = {
                  done: false,
                  log: slug,
                  dateFrom: dateFromPayload,
                  dateTo: dateToPayload,
                };
                this.pages.push(payload);
              }
            }
            this.assignWorkers();
          }
        }
      },
      assignWorkers() {
        this.workers = [];
        let workersNum = this.workersNumber;

        if (this.pages.length < workersNum) {
          workersNum = this.pages.length;
        }

        for (let i = 0; i < workersNum; i++) {
          this.workers.push(this.createWorker(i));
        }
      },
      createWorker(workerIndex) {
        const obj = {
          worker: new Worker(),
          isBusy: false,
        };
        obj.worker.onmessage = (result) => {
          const res = JSON.parse(result.data);
          if (res.items === 'error') {
            this.pages[res.index] = false;
          } else {
            this.onDataRecieved(res);
          }

          this.workers[workerIndex].isBusy = false;
        };

        return obj;
      },
      onDataRecieved(data) {
        this.pagesDone += 1;
        if (data.items) {
          data.items.forEach((item) => {
            item.ref = uuidv4();
            const logFields = this.logTypes[item.logId];
            if (logFields) {
              item.details = '<div>';
              logFields.forEach((fieldItem) => {
                let value = item[fieldItem.field];
                switch (item.logId) {
                  case 3: {
                    if (fieldItem.field === 'status') value = status(value);
                    break;
                  }
                  case 4: {
                    if (fieldItem.field === 'action') value = filesAction(value);
                    break;
                  }
                  case 5: {
                    if (fieldItem.field === 'type') value = clipboardType(value);
                    break;
                  }
                  case 7: {
                    if (fieldItem.field === 'action') value = instProgAction(value);
                    break;
                  }
                  case 8: {
                    if (fieldItem.field === 'action') value = drivesAction(value);
                    if (fieldItem.field === 'driveType') value = driveType(value);
                    break;
                  }
                  case 10: {
                    if (fieldItem.field === 'duration') value = convertSecondsToDays(value);
                    break;
                  }
                  case 11: {
                    if (fieldItem.field === 'action') value = computerAction(value);
                    break;
                  }
                  case 14: {
                    if (fieldItem.field === 'action') value = action(value);
                    if (fieldItem.field === 'available') value = available(value);
                    if (fieldItem.field === 'source') value = source(value);
                    break;
                  }
                  case 18: {
                    if (fieldItem.field === 'action') value = networkAction(value);
                    break;
                  }
                  case 19: {
                    if (fieldItem.field === 'action') value = netFilesAction(value);
                    break;
                  }
                  case 20: {
                    if (fieldItem.field === 'type') value = type(value);
                    break;
                  }
                  case 23: {
                    if (fieldItem.field === 'messenger') value = messengerName(value);
                    if (fieldItem.field === 'direction') value = directionName(value);
                    break;
                  }
                  case 25: {
                    if (fieldItem.field === 'action') value = hardwareAction(value);
                    break;
                  }
                  default:
                    break;
                }

                if (!value) value = '';

                item[fieldItem.field] = value;

                if (fieldItem.field === 'file') {
                  item.details += '';
                } else if (fieldItem.field === 'ssh') {
                  if (item.logId === 2) {
                    const url = getScreenshotLink(item.compId, item.path);
                    item.details += ` <img class="ssh" src="${url}">`;
                  } else if (item.logId === 5 && item.type === 2) {
                    const url = getClipboardFileLink(item.compId, item.id);
                    item.details += ` <img class="ssh" src="${url}">`;
                  }
                } else if (item.logId === 5 && fieldItem.field === 'text' && item.type !== this.$t('pages.text'))
                  item.details += '';
                else {
                  item.details += `<div class="detailsItem"> <div class="itemHeader">
                ${fieldItem.text}: </div> <div class="itemContent"> ${value} </div> </div>`;
                }
              });
              item.details += '</div>';
            }

            if (!this.isMapData && item.id > this.lastIds[item.logId][item.compId]) {
              item.isNew = true;

              if (item.logId === 23) {
                if (item.type === 'text') {
                  this.$store.dispatch('historyLogs/setLastId', {
                    id: item.id,
                    logTypeSlug: 'messegerstext',
                    idComputer: item.compId,
                  });
                }

                if (item.type === 'file') {
                  this.$store.dispatch('historyLogs/setLastId', {
                    id: item.id,
                    logTypeSlug: 'messegersfile',
                    idComputer: item.compId,
                  });
                }
              } else if (!this.isMapData) {
                const { slug } = logTypes.find((log) => log.id === item.logId);

                this.$store.dispatch('historyLogs/setLastId', {
                  id: item.id,
                  logTypeSlug: slug,
                  idComputer: item.compId,
                });
              }
            }
          });
          this.tableData.push(...data.items);
          this.tableData.sort((a, b) => new Date(a.date) - new Date(b.date));
        }
        if (this.pagesDone === this.pagesNumber) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);
        }
      },
      normalizer(node) {
        let { name } = node;
        if (node.nameUser) name = node.nameUser;
        return {
          label: name,
        };
      },
      async refresh() {
        await this.getFeed();
      },
      whenUpdated() {
        const { dateFrom, dateTo } = this.$route.query;
        if (dateFrom && dateTo) {
          this.$store.commit('filters/updateDateRange', [dateFrom, dateTo]);
        }

        const queryMutations = {};

        if (!dateFrom) {
          queryMutations.dateFrom = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
        }

        if (!dateTo) {
          queryMutations.dateTo = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        }

        if (Object.keys(queryMutations).length) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              ...queryMutations,
            },
          });
        }
      },
    },
  };
</script>

<style lang="scss" module>
  .pagination {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin: 10px 0;
  }

  .page2 {
    cursor: pointer;
    padding: 4px;
  }

  .pageActive2 {
    background-color: #2985bf;
    color: #fff;
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .page {
    background: $white;
    width: 100%;
  }

  .fit {
    height: fit-content;
  }

  .header {
    box-shadow: $shadow;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 20px;
    padding: 20px 30px;
  }

  .control {
    display: flex;
    align-items: flex-end;

    .mr {
      width: 40px;
      padding: 10px;
    }
  }

  .box {
    border: 1px solid #dfdfdf;
    height: 40px;
    display: flex;
    width: 40px;
    align-items: center;
    justify-content: center;
    margin: 0px 5px;
    background: #f5f5f5;
    cursor: pointer;
  }

  .activeBox {
    background: #e9f7ff;
    border-color: #b1d6ed;
  }

  .datePicker {
    margin-right: 20px;
  }

  .caretClosed {
    transform: rotate(180deg);
  }

  .button {
    border: 1px solid white;
    cursor: pointer;
    color: white;
    height: 36px;
    width: 10%;
    background: #2985bf;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 80px;
  }

  .disabledButton {
    opacity: 0.6;
  }

  .container {
    display: flex;
    flex: 1;
  }

  .wrapper {
    display: flex;
    justify-content: space-between;
    padding: 0 10px;
  }

  .modeName {
    display: flex;
    align-items: center;
    font-weight: 400;
    min-width: 145px;

    @media (max-width: 1500px) {
      display: none;
    }
  }

  .countComp {
    color: #2985bf;
    font-size: 11px;
    margin-left: 30px;
  }

  .saveIcon {
    width: 20px;
  }

  .loader {
    margin: 0 auto;
  }

  .ready {
    display: flex;
    gap: 5px;
    justify-content: center;
    align-items: center;
  }

  .hide {
    visibility: hidden;
  }
</style>

<style>
  .searchLogs {
    display: flex;
    flex-direction: column;
    width: 45%;
  }

  .searchLogs .vue-treeselect {
    margin-left: 2%;
  }

  .searchLogs .vue-treeselect__control {
    border-radius: 0px;
    width: 95%;
  }

  .searchLogs .vue-treeselect__menu {
    width: 95%;
    max-height: 160px;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .searchLogs .vue-treeselect__option {
    font-size: 14px;
  }

  .searchLogs .vue-treeselect__value-container {
    position: absolute;
    overflow: auto;
    height: 30px;
    scrollbar-width: none;
  }

  .searchLogs .vue-treeselect__value-container::-webkit-scrollbar {
    display: none;
  }

  .searchLogs .vue-treeselect__multi-value {
    margin: 3px 0px;
    width: 90%;
  }

  .searchLogs .vue-treeselect__multi-value-item-container {
    padding-top: 3px;
  }

  .searchLogs .iconFolder {
    display: inline-flex;
  }

  .searchLogs .vue-treeselect__checkbox--checked {
    background-color: #2985bf;
    border: 1px solid #2985bf;
  }

  .searchLogs .vue-treeselect__check-mark {
    margin: 0.5px;
  }

  .loader_loader_2-l8m {
    border: 5px solid #f5f5f5;
    border-top: 5px solid #2985bf;
    border-radius: 50%;
    width: 50px;
    height: 50px;
  }

  .detailsItem {
    display: flex;
  }

  .itemHeader {
    margin: 5px;
    font-weight: 500;
    min-width: 100px;
  }

  .itemContent {
    margin: 5px;
  }

  .ssh {
    width: 70%;
    margin: 7px;
  }

  .tableWithGrouping_searchResultContainer_2OxFS {
    height: 100%;
    background: white;
  }
</style>
